import { commandCenterPath } from '../commandCenterRoutes'
// ============================================================
// V2 Division Workspaces — the additive "organized doorway" layer.
//
// Each division is an organizational surface that LINKS to existing Square
// Code functionality (real, registered routes) or marks not-yet-wired
// functionality as PLANNED / SETUP-REQUIRED. This layer replaces nothing:
// every card is either a doorway to an existing route or an honest
// placeholder. Division set follows the operator placement map (9 divisions;
// olivium/vault/finance naming). This is a NEW mapping file — it does not
// rename or mutate the existing V2 command-center divisions.
//
// Honesty rule for this file:
//   kind: 'route'          → `to` is a REGISTERED, reachable route.
//   kind: 'planned'        → no route yet; not built.
//   kind: 'setup_required' → needs a provider/credential before it can run.
// Never mark a card 'route' unless the path is a real registered route.
// ============================================================

export type DivisionCardKind = 'route' | 'planned' | 'setup_required'

export interface DivisionCard {
  label: string
  kind: DivisionCardKind
  /** Present only for kind: 'route'. A real, registered internal path. */
  to?: string
  summary: string
}

export interface DivisionWorkspaceDef {
  id: string
  label: string
  accent: string
  tagline: string
  cards: DivisionCard[]
}

const R = (label: string, to: string, summary: string): DivisionCard => ({ label, kind: 'route', to, summary })
const P = (label: string, summary: string): DivisionCard => ({ label, kind: 'planned', summary })
const S = (label: string, summary: string): DivisionCard => ({ label, kind: 'setup_required', summary })

export const DIVISION_WORKSPACES: DivisionWorkspaceDef[] = [
  {
    id: 'strategy',
    label: 'Strategy',
    accent: '#0891b2',
    tagline: 'Market analysis, decisions, roadmaps, operator briefings.',
    cards: [
      P('Strategy Workspace', 'Strategy board — fill the lanes, watch readiness derive live, draft with governed agents. Retired from the legacy shell; its engine, records, and APIs stay live — the surface re-homes into this workspace in a follow-up lane.'),
      P('Decision Matrix', 'Weighted decision scoring across options — not wired yet.'),
      P('Roadmaps', 'Quarter/phase roadmap boards — not wired yet.'),
      P('Operator Briefings', 'Rolled-up briefing docs from division activity — not wired yet.'),
      P('Scenario Boards', 'Side-by-side scenario planning — not wired yet.'),
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    accent: '#2563eb',
    tagline: 'Discovery, intake, jobs, workflows, project setup.',
    cards: [
      R('Customer Discovery', commandCenterPath('intake'), 'V2 client send-off + private workspace link, Manage Invites, Review Queue (local preview).'),
      R('Client Intake', commandCenterPath('intake'), 'Secure guided client intake in the V2 discovery workroom — send-off, invite management, and the review queue backed by sq_intake_invites + sq_support_requests.'),
      R('Post Job', '/square/command-center/operations', 'Build-now execute layer — post a job, status timeline, approval gates, receipts.'),
      R('Workflows', '/square/command-center/operations', 'Workflow surface (n8n bridge deferred — see banner).'),
      R('Project Board', '/square/command-center/projects', 'Personal boards with agent managers and a send-to-job seam.'),
      R('Project Setup', commandCenterPath('projects'), 'V2 Projects portfolio — list, + New Project, project command deck (demo/local).'),
      P('Onboarding Center', 'Guided agent/client onboarding runs — not wired yet.'),
      P('Job Runs', 'Historical job run log scoped by project — not wired yet.'),
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    accent: '#059669',
    tagline: 'Models, forecasts, budgets, spreadsheets.',
    cards: [
      R('Finance Spreadsheet Workroom', commandCenterPath('finance/spreadsheet'), 'V2 prompt-to-spreadsheet — live local sheet + chart; export/share setup-required.'),
      P('Spreadsheet Builder', 'Prompt-to-grid workspace with a live local formula preview. Retired from the legacy shell; its engine, records, and APIs stay live — the surface re-homes into this workspace in a follow-up lane.'),
      P('Formula Library', 'Browsable formula packs — copy or open in the Spreadsheet Builder. Retired from the legacy shell; its engine, records, and APIs stay live — the surface re-homes into this workspace in a follow-up lane.'),
      S('Excel Integration', 'Excel Online / OneDrive export via Microsoft Graph — setup-required until credentials configured.'),
      P('Financial Models', 'Reusable model templates (CAC/LTV, unit economics) — not wired yet.'),
      P('Forecasts', 'Revenue / runway forecasts — not wired yet.'),
      P('Budgets', 'Budget vs. actuals tracking — not wired yet.'),
    ],
  },
  {
    id: 'advisory',
    label: 'Advisory',
    accent: '#6366f1',
    tagline: 'Agents, team, roles, permissions, approvals.',
    cards: [
      R('Agent Control Center', '/square/command-center/agents', 'Governed-agent management portal with real pause/activate enforcement.'),
      R('Agent Workspace', '/square/command-center/agents', 'Threads with agents, tasks, and the PR-style approval queue.'),
      R('Team Workspace', '/square/team-workspace', 'Internal-only collaboration with approval-gated agent drafts.'),
      R('Roles & People', '/square/security/people', 'Role and people management for the operator org.'),
      R('Permissions', '/square/security/roles', 'Role definitions and permission review.'),
      P('Approval Gates', 'Cross-division approval gate registry — not wired yet.'),
      P('Decision Review', 'Structured decision review + sign-off — not wired yet.'),
      P('Responsibility Matrix', 'RACI-style ownership matrix — not wired yet.'),
    ],
  },
  {
    id: 'comms',
    label: 'Comms',
    accent: '#7c3aed',
    tagline: 'Calendar, meetings, messages, notifications.',
    cards: [
      R('Message Center', commandCenterPath('communications'), 'The V2 Message Center — real membership-scoped channels + DMs, message-derived tasks/blockers (#104 spine).'),
      R('Conference Calls', commandCenterPath('communications/conference-calls'), 'Daily.co video conferencing — controlled rooms with scoped, short-lived join tokens (fails closed without DAILY_API_KEY). NOT the Daily Meetings record.'),
      R('Calendar', commandCenterPath('calendar'), 'V2 Quam Calendar workroom — month grid, day briefing, event workspace.'),
      R('Notifications', commandCenterPath('notifications'), 'The V2 Notifications center — live messages / tasks / calls / meetings lanes with real mark-read.'),
      S('Calls', 'Inbound/outbound calling — no provider selected yet (setup-required).'),
      P('Meeting Follow-ups', 'Auto follow-up + action items from meetings — not wired yet.'),
    ],
  },
  {
    id: 'creative',
    label: 'Creative',
    accent: '#ec4899',
    tagline: 'Documents, design, marketing, brand assets.',
    cards: [
      P('Creative Studio', 'Design creation execution engine — composes, renders SVG, halts at approval gates. Retired from the legacy shell; its engine, records, and APIs stay live — the surface re-homes into this workspace in a follow-up lane.'),
      P('Document Builder', 'Window-pane document workspace backed by the sq_workspaces engine. Retired from the legacy shell; its engine, records, and APIs stay live — the surface re-homes into this workspace in a follow-up lane.'),
      P('Inline Edit', 'Paragraph-level editing with local suggestion cards you preview and apply. Retired from the legacy shell; its engine, records, and APIs stay live — the surface re-homes into this workspace in a follow-up lane.'),
      P('Word-Doc Analysis', 'Read a Vault document with a local readability heuristic; stage a LandingAI run. Retired from the legacy shell; its engine, records, and APIs stay live — the surface re-homes into this workspace in a follow-up lane.'),
      P('Presentation Builder', 'Deck/pitch builder — not wired yet.'),
      P('Marketing Materials', 'Ads / flyers / social assets — not wired yet.'),
      P('Brand Assets', 'Brand kit + asset registry — not wired yet.'),
    ],
  },
  {
    id: 'olivium',
    label: 'Olivium',
    accent: '#dc2626',
    // PHASE_2M P8: every Olivium card stays INSIDE the V2 shell. Secure Access,
    // Portal Factory, and Project Portals are the live V2 surfaces; the audit/
    // chain/receipts/alerts/logs consoles are honest PLANNED V2 surfaces (their
    // legacy consoles remain reachable at /square/olivium/* but no V2 card
    // bounces the operator to the old screen).
    tagline: 'Security, access, audit, verification, receipts.',
    cards: [
      R('Secure Access', commandCenterPath('olivium/secure-access'), 'The V2 Secure Access hub — issue/revoke/expire hash-at-rest codes with universal target binding (live spine).'),
      R('Portal Factory', commandCenterPath('olivium/portal-factory'), 'Register a project, apply an Olivium portal shell, attach modules — real saved records.'),
      R('Project Portals', commandCenterPath('olivium/portals'), 'Every saved project portal — readiness, actions, blockers, receipts, context.'),
      P('Audit Trail', 'V2 audit feed is planned. The append-only olv_audit_trail keeps recording every privileged mutation today; the legacy console remains at /square/olivium/feed.'),
      P('Integrity Chain', 'V2 chain verification surface is planned; the legacy console remains at /square/olivium/chain.'),
      P('Receipts', 'A global V2 receipts view is planned; per-portal receipts are LIVE inside each project portal today.'),
      P('Risk Flags', 'V2 alerts surface is planned; the legacy console remains at /square/olivium/alerts.'),
      P('Access Logs', 'V2 session/access-log review is planned; the legacy console remains at /square/security/access-logs.'),
      P('Secure Share', 'Operator-authored secure share links — surfaced here once the module is powered.'),
      P('Permission Review', 'Periodic access re-certification — not wired yet.'),
    ],
  },
  {
    id: 'engineering',
    label: 'Engineering',
    accent: '#14b8a6',
    tagline: 'PRs, deploys, providers, health, logs.',
    cards: [
      P('PR Factory', 'Repository + PR visibility over the GitHub API, with operator-approved PR creation. Retired from the legacy shell; its engine, records, and APIs stay live — the surface re-homes into this workspace in a follow-up lane.'),
      R('Provider Setup', '/square/command-center/operations', 'Read-only provider status (Daily / ElevenLabs / Vercel / Supabase).'),
      R('Activation Doctor', '/square/command-center/agents', 'Self-reported deployment, env, database, Google redirect, and agent readiness.'),
      R('Usage Meter', '/square/command-center/operations', 'Rate/concurrency/token/credit policies with a live allow/queue/deny check.'),
      P('Deployments', 'Deploy history + rollbacks — not wired yet.'),
      S('GitHub', 'Repo/PR automation needs a server-side GITHUB_TOKEN (setup-required).'),
      P('API Health', 'Live endpoint health board — not wired yet.'),
      P('Logs', 'Aggregated platform logs — not wired yet.'),
    ],
  },
  {
    id: 'vault',
    label: 'Vault',
    accent: '#f59e0b',
    tagline: 'Files, uploads, private records, scoped secrets.',
    cards: [
      P('Files', 'Operator storage — uploads, signed file URLs (session-scoped). Retired from the legacy shell; its engine, records, and APIs stay live — the surface re-homes into this workspace in a follow-up lane.'),
      P('Google Workspace Vault', 'RETIRED by owner decision (2026-07-19) — the owner\'s own document system (Files / sq_documents) replaces the Google vault. Server-side Google/LandingAI APIs remain dormant; no UI surface exists.'),
      S('Scoped Secrets', 'Project-scoped secret registry — needs a secrets backend (setup-required).'),
      P('Uploads Queue', 'Pending upload + processing queue — not wired yet.'),
      P('Private Records', 'Encrypted private record store — not wired yet.'),
      P('Project Assets', 'Per-project asset registry — not wired yet.'),
      P('Seal Pending Queue', 'Artifacts awaiting an OLIVIUM vault seal — not wired yet.'),
    ],
  },
]

export function getDivisionWorkspace(id: string | undefined): DivisionWorkspaceDef | undefined {
  if (!id) return undefined
  return DIVISION_WORKSPACES.find((d) => d.id === id.toLowerCase())
}

export const DIVISION_WORKSPACE_IDS = DIVISION_WORKSPACES.map((d) => d.id)
